import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowDownIcon,
  CloseIcon,
  SidebarIcon,
  SidebarMenuIcon,
  UserCircleIcon,
} from 'shared/ui/icons';
import ImageLogo from 'shared/assets/images/logo_sembuh_ai.png';
import { useFeatureAccess, useMenus } from 'shared/lib/permissions';
import useWindowDimensions from 'shared/hooks/useWindowDimensions';
import { labelMenuSidebar } from 'shared/constants/sidebar';
import { useTranslation } from 'react-i18next';

import { UserInfo } from './user-info';
import { useSidebarUserInfo } from './hooks/useSidebarUserInfo';
import { buildMenuItems } from './sidebar-menu-items';

const PRICE_ESTIMATION_PATH = '/price-estimation';

// Menus whose active state extends to other routes (SA-1612: Policy Management
// lives under Company — keep the Company item active while on it).
const RELATED_ACTIVE_PATHS = {
  '/management-company': ['/policy-management'],
};

const cloneIconWithWhite = (icon) =>
  React.cloneElement(icon, { color: '#ffffff' });

// Menu items render as real anchors so right-click "Open in new tab",
// Ctrl/Cmd+click, and middle-click work natively. A plain left-click is
// intercepted for SPA navigation; modified clicks fall through to the browser.
const handleMenuNavigation = (event, navigate, path) => {
  if (!path) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
    return;
  }
  event.preventDefault();
  navigate(path);
};

const Sidebar = ({ smallSide, setSmallSide }) => {
  const { isMobile } = useWindowDimensions();
  const dataMenu = useMenus();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const userInfoProps = useSidebarUserInfo();
  const { isQualityCheckerActive } = useFeatureAccess();

  const [hideMenus, setHideMenus] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoveredChildIndex, setHoveredChildIndex] = useState(null);

  // Quality Checker and QC Dashboard Analytics both come from the BE menu list
  // (no FE-static injection). QC Dashboard Analytics stays at its BE position
  // and is hidden while the company's QC_CLAIM feature is off.
  const menuItems = useMemo(
    () => buildMenuItems(dataMenu, { showQcAnalytics: isQualityCheckerActive }),
    [dataMenu, isQualityCheckerActive]
  );

  const toggleMenu = (index) => {
    setHideMenus((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  useEffect(() => {
    if (!window.navigator.onLine) navigate('/no-network');
  }, [navigate]);

  const menuListProps = {
    menuItems,
    smallSide,
    isMobile,
    hideMenus,
    hoveredIndex,
    setHoveredIndex,
    hoveredChildIndex,
    setHoveredChildIndex,
    toggleMenu,
    navigate,
    location,
    t,
  };

  if (isMobile) {
    return (
      <div className="h-fit relative z-[100] w-full">
        <header className="flex justify-between items-center p-4 border-b border-gray-300 z-10 relative">
          <img src={ImageLogo} alt="logo_sembuh_ai" className="w-[120px]" />
          {smallSide ? (
            <SidebarMenuIcon
              className="cursor-pointer"
              onClick={() => setSmallSide(false)}
            />
          ) : (
            <CloseIcon
              className="cursor-pointer"
              onClick={() => setSmallSide(true)}
            />
          )}
        </header>
        {!smallSide && (
          <div className="bg-white w-full h-screen flex flex-col justify-between top-0 absolute p-4 pt-[70px]">
            <MenuList {...menuListProps} />
            <UserInfo {...userInfoProps} />
          </div>
        )}
      </div>
    );
  }

  return (
    <aside
      className={`${smallSide ? 'w-16' : 'col-span-2'} transition-all bg-neutral-900 text-slate-500 p-4 overflow-hidden border-r flex flex-col justify-between h-screen`}
    >
      <div
        className={`w-full flex ${smallSide ? 'justify-center' : 'justify-end'} mb-7`}
      >
        {!smallSide && (
          <img src={ImageLogo} alt="logo_sembuh_ai" className="w-auto hidden" />
        )}
        <button
          type="button"
          aria-label={smallSide ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setSmallSide(!smallSide)}
          className={`cursor-pointer ${smallSide ? 'rotate-180' : ''}`}
        >
          <SidebarIcon color="#ffffff" />
        </button>
      </div>
      <div className="overflow-y-auto flex-1">
        <MenuList {...menuListProps} />
      </div>

      {!smallSide ? (
        <UserInfo {...userInfoProps} />
      ) : (
        <div className="w-full flex justify-center items-center p-2">
          <div className="flex-none bg-white h-8 w-8 rounded-full flex items-center justify-center">
            <span>
              <UserCircleIcon />
            </span>
          </div>
        </div>
      )}
    </aside>
  );
};

function MenuList({
  menuItems,
  smallSide,
  isMobile,
  hideMenus,
  hoveredIndex,
  setHoveredIndex,
  hoveredChildIndex,
  setHoveredChildIndex,
  toggleMenu,
  navigate,
  location,
  t,
}) {
  return (
    <ul>
      {menuItems
        ?.filter((menu) => !menu.sidebarHide)
        ?.map((item, index) => (
          <MenuItem
            key={index}
            item={item}
            index={index}
            smallSide={smallSide}
            isMobile={isMobile}
            hideMenus={hideMenus}
            hoveredIndex={hoveredIndex}
            setHoveredIndex={setHoveredIndex}
            hoveredChildIndex={hoveredChildIndex}
            setHoveredChildIndex={setHoveredChildIndex}
            toggleMenu={toggleMenu}
            navigate={navigate}
            location={location}
            t={t}
          />
        ))}
    </ul>
  );
}

function MenuItem({
  item,
  index,
  smallSide,
  isMobile,
  hideMenus,
  hoveredIndex,
  setHoveredIndex,
  hoveredChildIndex,
  setHoveredChildIndex,
  toggleMenu,
  navigate,
  location,
  t,
}) {
  const matchesRelatedPath = (RELATED_ACTIVE_PATHS[item.path] ?? []).some(
    (relatedPath) => location.pathname.startsWith(relatedPath)
  );
  const isActive =
    location.pathname.startsWith(item.path) || matchesRelatedPath;
  const isPath = item.path;
  const key = item?.label?.toLowerCase().replace(/\s+/g, '-');
  const isPriceEstimation = isPath === PRICE_ESTIMATION_PATH;
  const showActiveIcon = hoveredIndex === index || isActive;
  const labelText = t(labelMenuSidebar?.[item?.label]) || item?.label;

  if (item.children) {
    return (
      <li
        key={'a' + key}
        id={'a' + key}
        className={`mb-2 mt-4 flex flex-col text-sm font-medium ${smallSide && 'items-center'} ${isMobile ? 'border-0' : ''}`}
      >
        {!smallSide ? (
          <>
            {isPriceEstimation && isActive && (
              <div className="relative">
                <div className="border-l border-4 rounded-tr-lg rounded-br-lg border-white h-6 absolute top-2 -left-0.5"></div>
              </div>
            )}
            <button
              type="button"
              aria-expanded={!hideMenus.includes(index)}
              onClick={() => toggleMenu(index)}
              className={`w-full flex justify-between items-center p-2 ${isPriceEstimation && isActive && 'rounded-md text-white bg-neutral-800'}`}
            >
              <span
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="flex text-sm items-center gap-2 cursor-pointer pl-[2px] hover:text-white"
              >
                {isPriceEstimation
                  ? showActiveIcon
                    ? cloneIconWithWhite(item.icon)
                    : item.icon
                  : ''}
                {labelText}
              </span>
              <ArrowDownIcon
                color={hoveredIndex === index ? '#ffffff' : '#667085'}
                className={`transform transition-transform cursor-pointer ${
                  !hideMenus.includes(index) ? 'rotate-180' : ''
                }`}
              />
            </button>
          </>
        ) : isPriceEstimation ? (
          <div className="flex items-center cursor-pointer justify-center">
            {showActiveIcon ? cloneIconWithWhite(item.icon) : item.icon}
          </div>
        ) : null}
        {item?.children && !hideMenus.includes(index) && (
          <div
            className="w-full flex flex-col mt-2 gap-2"
            key={'hide' + index}
            id={'hide' + index}
          >
            {item.children.map((child, childIndex) => (
              <MenuChildItem
                key={'child' + childIndex}
                child={child}
                childIndex={childIndex}
                smallSide={smallSide}
                isPriceEstimation={isPriceEstimation}
                hoveredChildIndex={hoveredChildIndex}
                setHoveredChildIndex={setHoveredChildIndex}
                navigate={navigate}
                location={location}
                t={t}
              />
            ))}
          </div>
        )}
      </li>
    );
  }

  return (
    <li
      key={'b' + key}
      id={'b' + key}
      className={`mb-2 ${smallSide && 'items-center'} flex flex-col text-sm font-medium py-2.5 px-2 ${!smallSide && isActive && 'rounded-md bg-neutral-800'} ${isMobile ? 'border-0' : ''}`}
    >
      {!smallSide && isActive && (
        <div className="relative">
          <div className="border-l border-4 rounded-tr-lg rounded-br-lg border-white h-6 absolute -left-2.5"></div>
        </div>
      )}
      <a
        href={isPath || '#'}
        onClick={(e) => handleMenuNavigation(e, navigate, item.path)}
        onMouseEnter={() => setHoveredIndex(index)}
        onMouseLeave={() => setHoveredIndex(null)}
        className={`w-full flex justify-between items-center text-sm gap-2 cursor-pointer no-underline ${isActive ? 'text-white' : 'hover:text-white text-gray-400'}`}
      >
        <span className="flex items-center gap-2">
          {showActiveIcon ? cloneIconWithWhite(item.icon) : item.icon}
          {!smallSide ? labelText : null}
        </span>
      </a>
    </li>
  );
}

function MenuChildItem({
  child,
  childIndex,
  smallSide,
  isPriceEstimation,
  hoveredChildIndex,
  setHoveredChildIndex,
  navigate,
  location,
  t,
}) {
  const matchesRelatedPath = (RELATED_ACTIVE_PATHS[child.path] ?? []).some(
    (relatedPath) => location.pathname.startsWith(relatedPath)
  );
  const isActive =
    location.pathname.startsWith(child.path) || matchesRelatedPath;
  const showActiveIcon = hoveredChildIndex === child?.id || isActive;

  if (!isPriceEstimation) {
    return (
      <a
        href={child?.frontend_endpoint || '#'}
        onClick={(e) =>
          handleMenuNavigation(e, navigate, child?.frontend_endpoint)
        }
        onMouseEnter={() => setHoveredChildIndex(child?.id)}
        onMouseLeave={() => setHoveredChildIndex(null)}
        key={'childv' + childIndex}
        id={'childv' + childIndex}
        className={`w-full text-left no-underline ${isActive ? 'text-gray-700 ' : 'hover:text-white text-gray-400'} ${!smallSide && isActive ? 'rounded-md text-white bg-neutral-800' : 'hover:text-gray-700 text-gray-400'} cursor-pointer flex p-2 items-center`}
      >
        {!smallSide && isActive && (
          <div className="relative">
            <div className="border-l border-4 rounded-tr-lg rounded-br-lg border-white h-6 absolute -left-2.5 -top-3"></div>
          </div>
        )}
        <div className="flex gap-2 items-center">
          {showActiveIcon ? cloneIconWithWhite(child?.icon) : child?.icon}
          {!smallSide ? t(labelMenuSidebar?.[child?.name] || child.name) : null}
        </div>
      </a>
    );
  }

  if (smallSide) return null;

  return (
    <a
      href={child?.frontend_endpoint || '#'}
      onClick={(e) =>
        handleMenuNavigation(e, navigate, child?.frontend_endpoint)
      }
      key={'childb' + childIndex}
      id={'childb' + childIndex}
      className={`w-full text-left no-underline ${isActive ? 'text-gray-700 ' : 'hover:text-white text-gray-400'} ${isActive ? 'rounded-md text-white' : 'hover:text-gray-700 text-gray-400'} cursor-pointer flex gap-2 p-2 items-center pl-9 ${child.slug === 'company-price-estimation-non-surgery' && 'hidden'}`}
    >
      {t(child.name)}
    </a>
  );
}

export default Sidebar;
